import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiEye, FiLock, FiMail } = FiIcons;

const Privacy = () => {
  React.useEffect(() => {
    document.title = "Privacy Policy at Melissa's Hairdressing Salon";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Melissa's Hairdressing Salon values your privacy. Learn how we protect your data and what information we collect.";
    }
  }, []);

  const privacySections = [
    {
      icon: FiEye,
      title: "What Information Do We Collect?",
      content: [
        "Personal Details: Such as your name, email address, and phone number when booking appointments.",
        "Usage Data: Information about how you interact with our website.",
        "Service Preferences: Your hair care needs, style preferences, and appointment history.",
        "Communication Records: Records of our interactions for service improvement."
      ]
    },
    {
      icon: FiShield,
      title: "How Do We Use Your Information?",
      content: [
        "Service Delivery: To arrange appointments and provide tailored services.",
        "Communication: To send service-related updates and offers.",
        "Improvement: To enhance user experience on our website.",
        "Legal Compliance: To meet our legal and regulatory obligations."
      ]
    },
    {
      icon: FiLock,
      title: "How We Protect Your Data",
      content: [
        "We use industry-standard security measures to protect your personal information.",
        "All data transmission is encrypted using SSL technology.",
        "Access to your personal information is restricted to authorized personnel only.",
        "We regularly update our security protocols to maintain the highest standards."
      ]
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-4 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <SafeIcon icon={FiShield} className="text-white text-3xl" />
            </div>
            <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Privacy Policy
            </h1>
            <h2 className="font-playfair text-2xl md:text-3xl font-semibold text-gray-700 mb-6">
              Your Privacy Matters to Us
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              At Melissa's Hairdressing Salon, we are committed to protecting your privacy and ensuring that your personal information is handled with care and respect.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Privacy Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {privacySections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg"
              >
                <div className="flex items-center mb-6">
                  <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-3 rounded-full mr-4">
                    <SafeIcon icon={section.icon} className="text-white text-xl" />
                  </div>
                  <h3 className="font-playfair text-2xl font-bold text-gray-900">
                    {section.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start text-gray-600">
                      <div className="w-2 h-2 bg-amber-400 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Your Choices Section */}
      <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="font-playfair text-4xl font-bold text-gray-900 mb-6">
              Your Choices
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              You have control over your personal information and how we use it.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                title: "Access & Update",
                description: "You can request to review and update your personal information at any time.",
                action: "Contact us to review your data"
              },
              {
                title: "Delete Your Data",
                description: "You have the right to request deletion of your personal information from our systems.",
                action: "Request data deletion"
              },
              {
                title: "Marketing Communications",
                description: "You can opt-out of promotional emails and marketing communications at any time.",
                action: "Unsubscribe from emails"
              },
              {
                title: "Data Portability",
                description: "You can request a copy of your personal data in a commonly used format.",
                action: "Download your data"
              }
            ].map((choice, index) => (
              <motion.div
                key={choice.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-lg"
              >
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-3">
                  {choice.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {choice.description}
                </p>
                <button className="text-amber-600 font-medium hover:text-amber-700 transition-colors">
                  {choice.action} →
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-r from-amber-600 to-rose-500 p-8 rounded-2xl text-center text-white"
          >
            <SafeIcon icon={FiMail} className="text-4xl mx-auto mb-4" />
            <h3 className="font-playfair text-2xl font-bold mb-4">
              Questions About Your Privacy?
            </h3>
            <p className="text-lg mb-6 opacity-90">
              For any queries or requests regarding your personal information, please don't hesitate to contact us.
            </p>
            <a 
              href="mailto:privacy@melissashair.com"
              className="bg-white text-amber-600 px-8 py-3 rounded-full font-medium text-lg hover:bg-gray-100 transition-all inline-block"
            >
              privacy@melissashair.com
            </a>
          </motion.div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600">
            <strong>Last Updated:</strong> December 2024
          </p>
          <p className="text-gray-500 mt-2">
            By continuing to use our site, you agree to our privacy practices.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Privacy;